import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useRevokeObjection } from 'hooks/useRevokeObjection';
import { RecordingObjectionScope, type RecordingObjectionStatus } from 'types/recordingObjection';
import { formatReadableDate } from 'utils';
import { getApiErrorMessage } from 'utils/apiError';
import {
  DisclaimerBox,
  DisclaimerSeparator,
  ObjectionAction,
  ObjectionIcon,
  ObjectionRow,
  ObjectionText,
} from './RecordingDisclaimer.styles';
import { RecordingObjectionRevokeDialog } from './RecordingObjectionRevokeDialog';

interface RecordingObjectionNoticeProps {
  objection: RecordingObjectionStatus;
  medatixxPatientId: string | undefined;
  isContinuingWithoutRecording: boolean;
}

export const RecordingObjectionNotice = ({
  objection,
  medatixxPatientId,
  isContinuingWithoutRecording,
}: RecordingObjectionNoticeProps) => {
  const { t } = useTranslation();
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const { mutate: revokeObjection, isPending } = useRevokeObjection(medatixxPatientId);
  const isPermanent = objection.scope === RecordingObjectionScope.Permanent;
  const permanentSince = isPermanent ? objection.objectedOn : null;
  const text = permanentSince
    ? t('recording.objection.activePermanent', { date: formatReadableDate(permanentSince) })
    : t('recording.objection.activeToday');

  const revoke = () =>
    revokeObjection(undefined, {
      onSuccess: () => setIsRevokeDialogOpen(false),
      onError: (error) => toast.error(getApiErrorMessage(error, t)),
    });

  return (
    <DisclaimerBox>
      <ObjectionRow role="status" data-ph-no-autocapture>
        <ObjectionIcon />
        <ObjectionText>{text}</ObjectionText>
        <ObjectionAction
          disabled={isPending || isContinuingWithoutRecording}
          onClick={isPermanent ? () => setIsRevokeDialogOpen(true) : revoke}
        >
          {t('recording.objection.revoke')}
        </ObjectionAction>
      </ObjectionRow>
      <DisclaimerSeparator />
      {isRevokeDialogOpen && (
        <RecordingObjectionRevokeDialog
          isRevoking={isPending}
          onConfirm={revoke}
          onClose={() => setIsRevokeDialogOpen(false)}
        />
      )}
    </DisclaimerBox>
  );
};
